import React, { Component } from 'react';
import connectWithStore from '../../hocs/connectWithStore';
import PropTypes from 'prop-types';
import StandingsTable from './standings_table';
import SeasonFilter from '../controls/filters/season_filter';
import TournamentFilter from '../controls/filters/tournament_filter';
import {
  setStandings,
  setLoading,
  setCurrentSeason,
  setCurrentTournament,
  setSeasons
} from './standings_actions';
import { buildLocalizedUrl } from '../../utils/locale';

class Standings extends Component {
  constructor(props) {
    super(props);
    // Seed the store from the server-rendered defaults so the initial React state
    // matches the SSR output and no redundant fetch fires on mount.
    props.setSeasons(props.init.seasons);
    props.setStandings(props.init.data);
    props.setCurrentSeason(props.init.defaultSeason);
    props.setCurrentTournament(props.init.defaultTournament);
  }

  componentDidUpdate(prevProps) {
    const { currentTournament, currentSeason } = this.props;
    if ((prevProps.currentTournament && currentTournament !== prevProps.currentTournament) ||
        (prevProps.currentSeason && currentSeason !== prevProps.currentSeason)) {
      this.fetchStandings(currentSeason);
    }
  }

  fetchStandings = async (seasonId) => {
    const { setLoading, setStandings, currentTournament } = this.props;
    if (!seasonId || !currentTournament) return;
    setLoading(true);
    try {
      const response = await fetch(`${buildLocalizedUrl('api/standings')}?season_id=${seasonId}&tournament_id=${currentTournament}`);
      const data = await response.json();
      setStandings(data);
    } catch (error) {
      console.error('Error fetching standings:', error);
    } finally {
      setLoading(false);
    }
  };

  handleSeasonChange = (seasonId) => {
    // Reconcile the tournament selection with the new season in the same handler so
    // both dispatches batch into a single re-render (and a single fetch): keep the
    // current tournament when it still has games in the new season, otherwise switch
    // to the first available. Every selectable season has tournaments (the server
    // only offers seasons with games), so there is always a valid option.
    const validTournaments = this.tournamentsForSeason(this.props.init.tournaments, seasonId);
    const currentStillValid = validTournaments.some(t => t.id === this.props.currentTournament);
    this.props.setCurrentSeason(seasonId);
    if (!currentStillValid && validTournaments.length > 0) {
      this.props.setCurrentTournament(validTournaments[0].id);
    }
  }

  handleTournamentChange = (selectedOption) => {
    this.props.setCurrentTournament(selectedOption.value);
    // No need to change season since seasons are global (not tournament-specific)
  }

  // Tournaments are loaded once for every season and tagged with the seasons they
  // have games in; filter them down to the currently selected season.
  tournamentsForSeason = (tournaments, seasonId) => {
    if (!seasonId) return tournaments;
    return tournaments.filter(t => (t.season_ids || []).includes(seasonId));
  }

  render() {
    const {
      init,
      seasons,
      t_controls,
      standings,
      loading,
      currentTournament,
      currentSeason
    } = this.props;

    // Seasons are global (team) seasons; no per-tournament filtering
    const filteredSeasons = seasons || init.seasons;
    const filteredTournaments = this.tournamentsForSeason(init.tournaments, currentSeason);
    return (
      <div className="standings-container">
        <article className="heading-component">
          <div className="heading-component-inner">
            <h5 className="heading-component-title">{t_controls.title}</h5>
            <div className="heading-component-aside">
              <ul className="list-inline list-inline-xs list-inline-middle">
                <TournamentFilter
                  tournaments={filteredTournaments}
                  tournamentSet={this.handleTournamentChange}
                  currentTournament={currentTournament}
                  t_controls={t_controls}
                />
                <SeasonFilter
                  seasons={filteredSeasons}
                  seasonSet={this.handleSeasonChange}
                  currentSeason={currentSeason}
                  t_controls={t_controls}
                  allIncluded={false}
                />
              </ul>
            </div>
          </div>
        </article>
        {loading ? (
          <div className="text-center p-4">{t_controls.loading}</div>
        ) : (
          <StandingsTable data={standings} t_controls={t_controls} />
        )}
      </div>
    );
  }
}

Standings.propTypes = {
  init: PropTypes.shape({
    data: PropTypes.array.isRequired,
    tournaments: PropTypes.array.isRequired,
    seasons: PropTypes.array.isRequired,
    defaultTournament: PropTypes.number.isRequired,
    defaultSeason: PropTypes.number.isRequired,
  }),
  t_controls: PropTypes.object.isRequired,
  standings: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  seasons: PropTypes.array,
  currentSeason: PropTypes.number,
  currentTournament: PropTypes.number,
  setStandings: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setCurrentSeason: PropTypes.func.isRequired,
  setCurrentTournament: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  standings: state.standings.standings,
  loading: state.standings.loading,
  currentSeason: state.standings.currentSeason,
  currentTournament: state.standings.currentTournament,
  seasons: state.standings.seasons
});

const mapDispatchToProps = {
  setStandings,
  setLoading,
  setCurrentSeason,
  setCurrentTournament,
  setSeasons
};

export default connectWithStore(
  mapStateToProps,
  mapDispatchToProps
)(Standings);